/**
 * A match's final score in the Dashboard and Matches lists.
 *
 * Everyone reads it ("2–1", or "—" when nobody has entered it). Priori
 * staff can set it in place, and — given `labeled`, what the GOAL labels add
 * up to (services/match_score) — see that beside it and accept it in one
 * click, or set their own and label the goals later (Ben, 2026-10-24). A
 * labeled score with goals on no team cannot be accepted: it does not say
 * whose they were. The row around it is a link into the match, so every
 * click in here stops at the cell.
 */
import { useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'

import { ApiError, api } from '../lib/api'
import './games.css'

export type Score = { home: number | null; away: number | null }
/** What the GOAL labels add up to: services/match_score.labeled. */
export type LabeledScore = { home: number; away: number; unsided: number }

const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation()

export function scoreText(s: Score) {
  return s.home != null && s.away != null ? `${s.home}–${s.away}` : '—'
}

/** The labeled score worth offering beside the stored one: there are goal
 *  labels, all on a team, and they say something the stored score does not. */
export function acceptable(labeled: LabeledScore | null | undefined, score: Score) {
  if (!labeled || labeled.unsided > 0) return false
  return labeled.home !== score.home || labeled.away !== score.away
}

export default function MatchScore({ gameId, score, canEdit, onSaved, labeled,
                                     showGoals = true }: {
  gameId: number | null
  score: Score
  canEdit: boolean
  onSaved: (s: Score) => void
  /** Staff: what the GOAL labels add up to (null or absent: none). */
  labeled?: LabeledScore | null
  /** Print "goals 2–1" beside the score. Off where the labeled score is
   *  already shown (Match Lab's header); accept still offers it. */
  showGoals?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [home, setHome] = useState('')
  const [away, setAway] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const post = async (h: number | null, a: number | null) => {
    if (busy) return                          // one save at a time
    setBusy(true); setError(null)
    try {
      const r = await api<{ home_score: number | null; away_score: number | null }>(
        `/api/v1/fixtures/games/${gameId}/score`,
        { method: 'POST', body: JSON.stringify({ home_score: h, away_score: a }) })
      onSaved({ home: r.home_score, away: r.away_score })
      setEditing(false)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }
  const goals = canEdit && labeled && labeled.home + labeled.away + labeled.unsided > 0
    ? labeled : null

  if (!editing) {
    const showHint = showGoals && goals
      && (goals.home !== score.home || goals.away !== score.away || goals.unsided > 0)
    const offer = gameId != null && acceptable(goals, score)
    // Two lines: the score and edit on the first, in the same place on every
    // row, so a column of scores lines up; what the goal labels say (and
    // accept) beneath it, only where there is something to say (Ben,
    // 2026-10-24).
    return (
      <span className="gm-score-stack">
        <span className="gm-score-cell">
          <span className="gm-score">{scoreText(score)}</span>
          {canEdit && gameId != null && (
            <button type="button" className="gm-score-edit" aria-label="Edit score"
                    onKeyDown={stop}
                    onClick={(e) => {
                      stop(e)
                      setHome(score.home?.toString() ?? '')
                      setAway(score.away?.toString() ?? '')
                      setError(null)
                      setEditing(true)
                    }}>
              edit
            </button>
          )}
        </span>
        {(showHint || offer || error) && (
          <span className="gm-score-cell gm-score-cell--sub">
            {showHint && (
              <span className="gm-score-goals"
                    title="What the goal labels add up to (Match Lab)">
                goals {goals!.home}–{goals!.away}
                {goals!.unsided > 0 && ` +${goals!.unsided} no team`}
              </span>
            )}
            {offer && (
              <button type="button" className="gm-score-edit" disabled={busy}
                      aria-label={`Accept the goal labels' score ${goals!.home}–${goals!.away}`}
                      onKeyDown={stop}
                      onClick={(e) => { stop(e); void post(goals!.home, goals!.away) }}>
                accept
              </button>
            )}
            {error && <span className="gm-score-error" role="alert">{error}</span>}
          </span>
        )}
      </span>
    )
  }

  // '' -> null, '0'..'99' -> a number, anything else -> undefined. Not
  // Number(v): NaN serializes as null, so two typos read as "clear".
  const parse = (v: string): number | null | undefined => {
    const t = v.trim()
    if (t === '') return null
    return /^\d{1,2}$/.test(t) ? Number(t) : undefined
  }
  const save = async () => {
    const h = parse(home), a = parse(away)
    if (h === undefined || a === undefined || (h === null) !== (a === null)) {
      setError('Enter both scores as whole numbers from 0 to 99, or leave both empty to clear.')
      return
    }
    await post(h, a)
  }
  const box = { width: '3.2em' }

  return (
    <span className="gm-score-cell" onClick={stop} onKeyDown={(e) => {
      stop(e)
      // Enter saves from the inputs only: on a button it is that button's
      // click (Cancel must not save first — SDET review of #896)
      if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') void save()
      if (e.key === 'Escape') { setEditing(false); setError(null) }
    }}>
      <input className="adm-input" style={box} inputMode="numeric"
             aria-label="Home score" value={home} autoFocus
             onChange={(e) => setHome(e.target.value)} />
      <span aria-hidden>–</span>
      <input className="adm-input" style={box} inputMode="numeric"
             aria-label="Away score" value={away}
             onChange={(e) => setAway(e.target.value)} />
      {goals && goals.unsided === 0 && (
        <button type="button" className="adm-btn adm-btn--quiet"
                onClick={() => { setHome(String(goals.home)); setAway(String(goals.away)) }}>
          Use goals {goals.home}–{goals.away}
        </button>
      )}
      <button type="button" className="adm-btn" disabled={busy}
              onClick={() => void save()}>Save</button>
      <button type="button" className="adm-btn adm-btn--quiet"
              onClick={() => { setEditing(false); setError(null) }}>Cancel</button>
      {error && <span className="gm-score-error" role="alert">{error}</span>}
    </span>
  )
}
